import React from 'react';
import { useHistory } from 'react-router';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../context';
import { useAlert, useFetch, useToggle } from '../../hooks';
import { Title } from '../../styles';
import {
    Field,
    Form,
    FormType,
    InputEventProps,
    ActionButton,
    Alert
} from '..';

export interface ForceChangePasswordValuesProps {
    NewPassword: string;
    ConfirmPassword: string;
}

interface forceChangePasswordDetailsProps {
    NewPassword: string;
    ConfirmPassword: string;
}

const forceChangePasswordDetailsInitial: forceChangePasswordDetailsProps = {
    NewPassword: '',
    ConfirmPassword: ''
};

export const ForceChangePassword: React.FC = React.memo(() => {
    const history = useHistory();
    const [fetchHandler, loading] = useFetch('/utForcePasswordChange', 'POST');
    const { alert, dispatchAlert, closeAlert } = useAlert();
    const { session, dispatchAuth } = useAuth();
    const [forceChangePasswordDetails, setForceChangePasswordDetails] =
        React.useState<forceChangePasswordDetailsProps>(
            forceChangePasswordDetailsInitial
        );
    const [showPassword, togglePassword] = useToggle(false);

    const onChangeHandler = React.useCallback(
        (key: keyof forceChangePasswordDetailsProps) => (e: InputEventProps) =>
            setForceChangePasswordDetails(prevForceChangePasswordDetails => {
                return {
                    ...prevForceChangePasswordDetails,
                    [key]: e.target.value
                };
            }),
        []
    );

    const successHandler = React.useCallback(() => {
        dispatchAlert({
            type: 'UPDATE_ALERT',
            message: 'Success!',
            severity: 'success'
        });
        dispatchAuth({
            type: 'UPDATE_FORCE_CHANGE_PASSWORD',
            state: 0
        });
        history.push('/login');
    }, [dispatchAlert, dispatchAuth, history]);

    const errorHandler = React.useCallback(
        message => {
            dispatchAlert({
                type: 'UPDATE_ALERT',
                message: message,
                severity: 'error'
            });
            setForceChangePasswordDetails(forceChangePasswordDetailsInitial);
        },
        [dispatchAlert]
    );

    const forceChangePasswordHandler = React.useCallback(
        async (e: FormType) => {
            e.preventDefault();
            const result = await fetchHandler({
                data: {
                    member_guid: session.MemberGuid,
                    session_guid: session.SessionGuid,
                    data: forceChangePasswordDetails
                }
            });
            result?.success ? successHandler() : errorHandler(result.msg);
            dispatchAlert({ type: 'OPEN' });
        },
        [
            dispatchAlert,
            errorHandler,
            forceChangePasswordDetails,
            fetchHandler,
            session.MemberGuid,
            session.SessionGuid,
            successHandler
        ]
    );

    return (
        <>
            <Alert closeHandler={closeAlert} {...alert} />
            <Container
                component='section'
                maxWidth='md'
                sx={{ m: '1rem auto' }}
            >
                <Title component='h1' marginBottom='2rem'>
                    Force Change Password
                </Title>
                <Form onSubmit={forceChangePasswordHandler}>
                    <Field
                        name='NewPassword'
                        label='New Password'
                        onChange={onChangeHandler('NewPassword')}
                        endAdornment={
                            <IconButton onClick={togglePassword}>
                                {showPassword ? (
                                    <VisibilityIcon />
                                ) : (
                                    <VisibilityOffIcon />
                                )}
                            </IconButton>
                        }
                    />
                    <Field
                        name='ConfirmPassword'
                        label='Confirm Password'
                        onChange={onChangeHandler('ConfirmPassword')}
                        endAdornment={
                            <IconButton onClick={togglePassword}>
                                {showPassword ? (
                                    <VisibilityIcon />
                                ) : (
                                    <VisibilityOffIcon />
                                )}
                            </IconButton>
                        }
                    />
                    <ActionButton
                        type='submit'
                        loading={loading}
                        title='Submit'
                        grid
                    />
                </Form>
            </Container>
        </>
    );
});
